/**
 * PLM — Daily QC: the daily reference-slide read (prototype PLM-Analysis-v12/v13,
 * 2026-09-28; owner decision "Enforce the lock (v13)"; G5 behaviour ported
 * 2026-10-16 — owner: "move G5 behaviour to G6", PlmV2DailyQcController).
 *
 * Before an analyst's first PLM analysis of the day they read a slide from the
 * PLM Reference Library. Left: the slide picker (ONE row per slide — a slide
 * holding several asbestos types is read for all of them; Last Run / Days Ago,
 * ⚠ when read within 7 days — flagged, never blocked) or the Daily QC records.
 * Right: the read's header (PLM Scope + devices, Scope Aligned, the slide's
 * Reference / Matrix / Source) and the SAME entry tabs a layer uses
 * (Stereoscopic Observation · PLM Exam) plus the QC Result View.
 *
 * Browsing (G5 2026-10-17): Previous / Next walk every analyst's reads, newest
 * first, and open them read-only (View). The analyst's own pending read is
 * editable when opened from the list or New; reached while browsing it stays in
 * View — the form has no Edit (owner 2026-10-19, G5 parity): the list's Edit
 * opens the read for entry. Save / Submit / Reset only while editing.
 *
 * Reference Slide ID (owner 2026-10-19, G5 parity): on the analyst's own pending
 * read being entered it is a pick list of the slides New offers; Save moves the
 * read to the slide picked (server-side, audited) and the form reloads on it.
 * New is disabled while the analyst's latest read of today is not submitted.
 *
 * Flow (G5): enter the read → Save (refused while a required field is missing)
 * → Submit (saves an unsaved read first). Only then does the QC Result View show
 * PASS / FAIL, the checks and one row per reference type — never after a Save, so
 * the answer cannot be changed after seeing it (owner 2026-10-20). A passed read
 * releases the analyst's PLM layers for the day; a failed one blocks the day
 * until a re-read passes or a supervisor overrides.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuthStore } from '../../../auth/store';
import { useNavCollapse } from '../../../hooks/useNavCollapse';
import { AppToolbar } from '../../../components/AppToolbar';
import { AuditTrailButton } from '../../../components/AuditTrailButton';
import { FormTitle } from '../../../components/FormTitle';
import { showDialog, useConfirm, useDeleteFlow, useSaveFlow } from '../../../components/confirm';
import { useHotkeys, formatCombo, effectiveCombo, type HotkeyDef } from '../../../components/hotkeys';
import { useNotify } from '../../../components/notifications';
import { useUnsavedGuard } from '../../../hooks/useUnsavedGuard';
import { useNavActions } from '../../../hooks/useNavActions';
import { fmtLocalDate, fmtLocalDateTime, nowClientWire } from '../../../utils/datetime';
import { ComboSelect } from '../../../components/ComboSelect';
import { LookupCombo } from '../../../components/LookupCombo';
import {
  commentPlmDailyQc, deletePlmDailyQc, fetchPlmDailyQc, fetchPlmDailyQcList, fetchPlmDailyQcSlides,
  fetchPlmDailyQcStatus, fetchPlmFiberTypeValues, fetchPlmLookups, fetchPlmStationRows, fetchPlmUtilities,
  overridePlmDailyQc, rollbackPlmDailyQc, checkRollbackPlmDailyQc, savePlmDailyQc, submitPlmDailyQc, EMPTY_PLM_UTILITIES,
  type PlmDailyQcDetail, type PlmDailyQcRow, type PlmDailyQcSlide, type PlmDailyQcStatus,
  type PlmExamInput, type PlmFiberTypeValue, type PlmLookups, type PlmObservationInput, type PlmStationRow,
  type PlmUtilityLists,
} from './plm.api';
import { balanceResult, blankExam, isBlankExam } from './plmAutofill';
import {
  allowedBy, blankObservation, dailyDeleteMessage, dailyMissingFields, dailyOrder, dailyViewReason, missingFieldsWarning, num3,
  refusalText, submitConfirmText, submittedText, DAILY_DELETE_REASON, DAILY_DELETE_LOCKED_TIP,
  DAILY_NEW_TIP, dailyNewBlockedTip, dailySlideLookup, slideLabel,
} from './plmDailyQc';
import { PlmDeviceCombo, PlmDevicePick, deviceNames } from './PlmDeviceFields';
import { PlmExamTab } from './PlmExamTab';
import { PlmQcChartsPage } from './PlmQcChartsPage';
import { PlmTopbarMeta } from './PlmTopbarMeta';
import { PlmDailySlidePickerDialog } from './PlmDailySlidePickerDialog';
import { PlmStereoTab } from './PlmStereoTab';
import './plm.css';

const AUDIT_FORM = 'PLM';
const EMPTY_LOOKUPS: PlmLookups = {
  fiberTypes: [], componentNames: [], fiberColors: [], materials: [], sampleNotes: [], analysts: [],
  dispersionOils: [],
};
/** The Daily QC list's nav id — its MAC Delete right gates Delete. */
const NAV_ID = 'plm-dailyqc';
const same = (a: string | null | undefined, b: string | null | undefined) =>
  !!a && !!b && a.toLowerCase() === b.toLowerCase();

/** The device fields of the read's header — the four of PLM Result Entry, in its
 *  order and words (G5 PLMV2_DailyEntry 2026-10-17; PLM Scope is required). Hot
 *  Plate / Thermohydrometer are kept in the read's ObservationJson as `hotPlate` /
 *  `thermohydrometer`, the keys G5 PlmV2Store reads and writes. */
const DEVICES = [
  ['microscopeId', 'PLM Scope', true],
  ['microscope', 'Stereo Microscope', false],
  ['hotPlate', 'Hot Plate', false],
  ['thermohydrometer', 'Thermohydrometer', false],
] as const;

export interface PlmDailyQcPageProps {
  /** Open this Daily QC; omit to start on the slide picker (+ New). */
  oid?: string | null;
  onBack: () => void;
}

export default function PlmDailyQcPage({ oid: initialOid = null, onBack }: PlmDailyQcPageProps) {
  const me = useAuthStore(s => s.user?.id ?? null);
  const notify = useNotify();
  const { confirm, prompt } = useConfirm();
  const runSave = useSaveFlow();
  const runDelete = useDeleteFlow();
  const perm = useNavActions(NAV_ID);

  const [oid, setOid] = useState<string | null>(initialOid);
  const [picking, setPicking] = useState(!initialOid);
  const [records, setRecords] = useState<PlmDailyQcRow[]>([]);
  const [status, setStatus] = useState<PlmDailyQcStatus | null>(null);
  const [detail, setDetail] = useState<PlmDailyQcDetail | null>(null);
  const [lookups, setLookups] = useState<PlmLookups>(EMPTY_LOOKUPS);
  const [fiberTypeValues, setFiberTypeValues] = useState<PlmFiberTypeValue[]>([]);
  const [stationRows, setStationRows] = useState<PlmStationRow[]>([]);
  // The remembered device names (dbo.AI_PlmUtility) — offered when there are no setup rows.
  const [utilities, setUtilities] = useState<PlmUtilityLists>(EMPTY_PLM_UTILITIES);
  // G5 Enter / View: a read opened from the list or New is entered (when it is the
  // analyst's own pending read); one reached with Previous / Next is browsed.
  const [entering, setEntering] = useState(true);
  const [draft, setDraft] = useState<PlmObservationInput | null>(null);
  const [scopeAligned, setScopeAligned] = useState(false);
  const [exams, setExams] = useState<PlmExamInput[]>([]);
  const [examIndex, setExamIndex] = useState(0);
  const [tab, setTab] = useState<'stereo' | 'exam' | 'result'>('stereo');
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadMs, setLoadMs] = useState<number | null>(null);
  const [chartsOpen, setChartsOpen] = useState(false);
  // Reference Slide ID pick list (owner 2026-10-19, G5 parity): the slides New offers,
  // and the one picked (null = the read's own slide; Save moves the read to it).
  const [slides, setSlides] = useState<PlmDailyQcSlide[]>([]);
  const [pickedSlide, setPickedSlide] = useState<PlmDailyQcSlide | null>(null);
  useUnsavedGuard(dirty);

  // G5 folds the navigation panel away when the Daily QC read opens
  // (PlmV2DailyQcController.OnActivated, owner request 2026-10-17). The COUNTED
  // shared hook (2026-11-03): a read opened from a PLM batch no longer reopens
  // the nav when it closes — the batch still holds it.
  useNavCollapse();

  const loadLists = useCallback(async () => {
    const [r, s] = await Promise.all([fetchPlmDailyQcList(), fetchPlmDailyQcStatus()]);
    setRecords(r);
    setStatus(s);
  }, []);

  useEffect(() => {
    let live = true;
    void (async () => {
      const t0 = performance.now();
      setLoading(true);
      try {
        const [lk, ftv] = await Promise.all([fetchPlmLookups(), fetchPlmFiberTypeValues()]);
        if (!live) return;
        setLookups(lk);
        setFiberTypeValues(ftv);
        // The PLM Instrument Setup rows the device drop-downs list (as on the entry header).
        void fetchPlmStationRows().then(rows => { if (live) setStationRows(rows ?? []); })
          .catch(() => { if (live) setStationRows([]); });
        void fetchPlmUtilities().then(u => { if (live) setUtilities(u); })
          .catch(() => { if (live) setUtilities(EMPTY_PLM_UTILITIES); });
        await loadLists();
      } finally {
        if (live) { setLoading(false); setLoadMs(performance.now() - t0); }
      }
    })();
    return () => { live = false; };
  }, [loadLists]);

  /** `tab`: 'result' brings the QC Result View forward (after Submit), 'keep' stays on
   *  the tab the analyst is on (after Save), 'auto' opens a submitted read on its result. */
  const loadRecord = useCallback(async (id: string, tab: 'auto' | 'result' | 'keep' = 'auto') => {
    const d = await fetchPlmDailyQc(id);
    setDetail(d);
    if (!d) return;
    // G5 PlmV2DailyQcEntry.Build: a read starts with the record's analyst, and with
    // no analysed time yet it takes now (today's read) or the read's own date.
    const base = d.observation ?? blankObservation(d.record.analystOid, d.record.qcDate);
    const untimed = !base.analyzedDate || base.analyzedDate.slice(11, 19) === '00:00:00';
    const today = nowClientWire().slice(0, 10);
    setDraft({
      ...base,
      analyzedByOid: base.analyzedByOid ?? d.record.analystOid,
      analyzedDate: untimed
        ? (d.record.qcDate.slice(0, 10) === today ? nowClientWire() : d.record.qcDate)
        : base.analyzedDate,
    });
    setScopeAligned(!!d.scopeAligned);
    // One empty row ready to type into, as the layer grid does.
    setExams(d.exams.length ? d.exams : [blankExam(1)]);
    setExamIndex(0);
    setPickedSlide(null);
    setDirty(false);
    if (tab !== 'keep') setTab(tab === 'result' || d.record.submittedAt ? 'result' : 'stereo');
  }, []);

  useEffect(() => { if (oid) void loadRecord(oid); }, [oid, loadRecord]);

  const rec = detail?.record ?? null;
  const mine = !!rec && same(rec.analystOid, me);

  // Names itself in the app top bar, as the prototype does for a QC record
  // (v13 titleText: "PLM QC – <id>"); the slide picker reads "PLM QC – Daily QC".
  const topbarTitle = !picking && rec?.dailyQcId ? `PLM QC – ${rec.dailyQcId}` : 'PLM QC – Daily QC';
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('ai:topbar-title', { detail: topbarTitle }));
    return () => { window.dispatchEvent(new CustomEvent('ai:topbar-title', { detail: null })); };
  }, [topbarTitle]);
  const editable = mine && !rec?.submittedAt && entering;
  // The pick list is loaded while the read may be changed — the same list as New (G5 ReferenceChoices).
  useEffect(() => {
    if (!editable) return;
    let live = true;
    fetchPlmDailyQcSlides()
      .then(list => { if (live) setSlides(list); })
      .catch(() => { if (live) setSlides([]); });
    return () => { live = false; };
  }, [editable]);
  const slideLookup = useMemo(() => dailySlideLookup(slides, rec ? {
    referenceId: rec.referenceId, originalSourceId: rec.originalSourceId,
    matrix: detail?.slideMatrix ?? null, source: detail?.slideSource ?? null,
  } : null), [slides, rec, detail?.slideMatrix, detail?.slideSource]);
  // New is off while the analyst's latest read of today is not submitted (G5 GateNew).
  const pendingId = status?.pendingDailyQcId ?? null;
  const viewReason = !rec ? null : dailyViewReason(rec, mine, entering);
  // Override is offered on the analyst's LATEST read when it failed, to a user
  // with the override permission. The server re-checks it is TODAY's latest read
  // and not already overridden.
  const latestOfAnalyst = useMemo(() => (rec ? records
    .filter(r => same(r.analystOid, rec.analystOid))
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0] : undefined), [records, rec]);
  const canOverrideThis = !!rec && !!status?.canOverride && rec.result === 'Fail'
    && latestOfAnalyst?.oid === rec.oid
    && !(same(rec.analystOid, me) && status.state === 'override');

  // Previous / Next walk the records newest Daily QC ID first (G5 `Order`).
  const order = useMemo(() => dailyOrder(records), [records]);
  const index = rec ? order.findIndex(r => r.oid === rec.oid) : -1;

  // ── actions ────────────────────────────────────────────────────────────────

  /** Runs `go` unless the read holds unsaved changes the user wants to keep (G5 `Leave`). */
  const leave = useCallback(async (go: () => void | Promise<void>) => {
    if (dirty && editable && !(await confirm({ title: 'Unsaved changes', message: 'Unsaved changes will be lost. Do you want to proceed?' }))) return;
    if (dirty) setDirty(false);
    await go();
  }, [dirty, editable, confirm]);

  /** New (G5): today's open read is offered instead; otherwise the slide picker. */
  const doNew = useCallback(() => leave(async () => {
    const open = status?.state === 'pending' ? status.today : null;
    if (open) {
      if (await confirm({ title: 'New', message: `You already have today's Daily QC ${open.dailyQcId ?? ''} in progress. Open it instead?` })) {
        setPicking(false);
        setEntering(true);
        if (open.oid === oid) await loadRecord(open.oid); else setOid(open.oid);
      }
      return;
    }
    setPicking(true);
  }), [leave, status, rec, me, confirm, oid, loadRecord]);

  const payload = useCallback(() => ({
    observation: draft!, exams: exams.filter(e => !isBlankExam(e)), scopeAligned,
    // A slide picked in Reference Slide ID: the server moves the read to it first (owner 2026-10-19).
    referenceSampleOid: pickedSlide?.oid ?? null,
  }), [draft, exams, scopeAligned, pickedSlide]);

  /** A Save refused for missing fields shows an amber Warning listing each field and where it is,
   *  and opens the tab of the first one (owner 2026-11-03: "Please complete: Friability" in a red
   *  Error was not friendly). False = not that refusal; the caller shows its usual error. */
  const showMissingFields = useCallback((e: unknown): boolean => {
    const fields = dailyMissingFields(e);
    if (!fields) return false;
    const w = missingFieldsWarning(fields);
    if (w.tab) setTab(w.tab);
    void showDialog({ type: 'warning', title: w.title, message: w.message, list: w.list });
    return true;
  }, []);

  const doSave = useCallback(async (): Promise<boolean> => {
    if (!oid || !draft || !editable) return false;
    return runSave({
      formName: AUDIT_FORM,
      auditAction: 'edit',
      save: async () => { await savePlmDailyQc(oid, payload()); },
      // No result after a Save (owner 2026-10-20): the analyst stays on the tab they are on.
      // Reloaded, so a read moved to another slide shows its Matrix / Source.
      onSaved: () => { void loadRecord(oid, 'keep'); if (pickedSlide) void loadLists(); },
      onError: e => {
        if (!showMissingFields(e)) notify.error(refusalText(e, 'The Daily QC could not be saved. Try Refresh, and if it keeps happening report it.'));
      },
    });
  }, [oid, draft, editable, runSave, loadRecord, loadLists, payload, pickedSlide, showMissingFields, notify]);

  const doSubmit = useCallback(async () => {
    if (!oid || !editable || !rec) return;
    // No result in the question: Pass / Fail is shown only after Submit (owner 2026-10-20).
    const ok = await confirm({
      title: 'Submit',
      message: submitConfirmText(rec.dailyQcId, null, allowedBy(status, rec)),
      confirmLabel: 'Submit',
    });
    if (!ok) return;
    setBusy(true);
    try {
      // Submit saves an unsaved read first, so what is graded is what is on the screen (G5).
      if (dirty || !detail?.saved) {
        try {
          await savePlmDailyQc(oid, payload());
          setDirty(false);
        } catch (e) {
          if (!showMissingFields(e)) notify.error(refusalText(e, 'The Daily QC could not be saved. Try Refresh, and if it keeps happening report it.'));
          return;
        }
      }
      const res = await submitPlmDailyQc(oid);
      const msg = submittedText(res.result, res.blocked);
      if (msg.tone === 'success') notify.success(msg.text); else notify.warning(msg.text);
      await Promise.all([loadRecord(oid, 'result'), loadLists()]);
    } catch (e) {
      notify.error(refusalText(e, 'The Daily QC could not be submitted. Try Refresh, and if it keeps happening report it.'));
    } finally {
      setBusy(false);
    }
  }, [oid, editable, rec, dirty, detail, status, confirm, notify, payload, loadRecord, loadLists, showMissingFields]);

  const doReset = useCallback(async () => {
    if (!oid || !editable) return;
    if (!(await confirm({ title: 'Reset', message: 'Discard all unsaved changes on this Daily QC?' }))) return;
    await loadRecord(oid);
    notify.success('Reset completed successfully.');
  }, [oid, editable, confirm, loadRecord, notify]);

  const doComment = useCallback(async () => {
    if (!oid || !rec) return;
    const text = await prompt({
      title: 'Comment',
      message: `Comment for ${rec.dailyQcId ?? 'this Daily QC'}:`,
      placeholder: 'Comment',
      initialValue: detail?.comment ?? '',
      required: true,
      confirmLabel: 'Save',
    });
    if (!text?.trim()) return;
    try {
      await commentPlmDailyQc(oid, text.trim());
      setDetail(d => (d ? { ...d, comment: text.trim() } : d));
      notify.success('Comment saved successfully.');
    } catch (e) {
      notify.error(refusalText(e, 'The comment could not be saved. Try Refresh, and if it keeps happening report it.'));
    }
  }, [oid, rec, detail?.comment, prompt, notify]);

  const doRollback = useCallback(async () => {
    // Anyone may roll back a submitted read, as on QC Batch (owner 2026-10-19).
    if (!oid || !rec?.submittedAt) return;
    // Refused up front when a PLM layer already uses the read (owner 2026-10-21).
    try { await checkRollbackPlmDailyQc(oid); }
    catch (e) {
      await showDialog({ type: 'error', title: 'Rollback', message: refusalText(e, 'This Daily QC cannot be rolled back.') });
      return;
    }
    const reason = await prompt({
      title: 'Rollback',
      message: `Re-open the submitted Daily QC ${rec.dailyQcId ?? ''}? Reason (required — audited):`,
      placeholder: 'Reason',
      required: true,
      confirmLabel: 'Rollback',
    });
    if (!reason?.trim()) return;
    setBusy(true);
    try {
      await rollbackPlmDailyQc(oid, reason.trim());
      notify.success('Rollback completed successfully.');
      await Promise.all([loadRecord(oid), loadLists()]);
    } catch (e) {
      notify.error(refusalText(e, 'The Daily QC could not be re-opened. Try Refresh, and if it keeps happening report it.'));
    } finally {
      setBusy(false);
    }
  }, [oid, rec, prompt, notify, loadRecord, loadLists]);

  const doOverride = useCallback(async () => {
    if (!rec) return;
    const reason = await prompt({
      title: 'Supervisor override',
      message: `Allow ${rec.analystName ?? 'this analyst'} to analyze today although the Daily QC failed? `
        + 'Reason + corrective action (required — audited):',
      placeholder: 'Reason + corrective action',
      required: true,
      confirmLabel: 'Override',
    });
    if (!reason?.trim()) return;
    setBusy(true);
    try {
      await overridePlmDailyQc(rec.analystOid, reason.trim());
      notify.success('Override completed successfully.');
      await loadLists();
    } catch (e) {
      notify.error(refusalText(e, 'The Daily QC could not be overridden. Try Refresh, and if it keeps happening report it.'));
    } finally {
      setBusy(false);
    }
  }, [rec, prompt, notify, loadLists]);

  // Delete (2026-10-17, the G5 change): a PENDING read that no PLM layer uses, by
  // its own analyst (the server also lets an administrator). Soft delete, reason
  // required and audited; the server re-checks every rule.
  const canDeleteThis = !!rec && !rec.submittedAt && !detail?.used && !!detail?.canDelete;
  const doDelete = useCallback(async () => {
    if (!oid || !rec || !canDeleteThis) return;
    // The next record to open: the one after it in the Previous / Next order, else the one before.
    const nextOid = (order[index + 1] ?? order[index - 1])?.oid ?? null;
    await runDelete({
      formName: AUDIT_FORM,
      message: dailyDeleteMessage(rec.dailyQcId),
      reasonRequired: DAILY_DELETE_REASON,
      successMessage: 'Delete completed successfully.',
      delete: async () => { await deletePlmDailyQc(oid); },
      onDeleted: async () => {
        setDirty(false);
        await loadLists();
        // G5: the next read opens in View mode.
        setEntering(false);
        if (nextOid) setOid(nextOid);
        else { setOid(null); setDetail(null); setDraft(null); }
      },
    });
  }, [oid, rec, canDeleteThis, order, index, runDelete, loadLists]);

  // Walking the list browses the history: the read opens in View mode (G5 Step).
  const step = useCallback((dir: -1 | 1) => {
    const target = order[index + dir];
    if (index < 0 || !target || (target.oid === oid && !picking)) return;
    void leave(() => { setPicking(false); setEntering(false); setOid(target.oid); });
  }, [order, index, oid, picking, leave]);

  // No Edit on the form (owner 2026-10-19, G5 parity: PlmV2DailyQcController hides
  // it) — the Daily QC list's Edit opens the analyst's own pending read for entry.

  // ── exam grid handlers (the entry page's, minus the point count) ───────────

  const edit = <T,>(fn: (prev: T) => T, set: (f: (prev: T) => T) => void) => { set(fn); setDirty(true); };
  const addExam = (init?: Pick<PlmExamInput, 'fiberTypeOid'>) => edit<PlmExamInput[]>(prev => {
    const next = [...prev, { ...blankExam(prev.length + 1), ...init, result: balanceResult(prev) }];
    setExamIndex(next.length - 1);
    return next;
  }, setExams);
  const addExamAt = (i: number) => edit<PlmExamInput[]>(prev => {
    const next = [...prev.slice(0, i + 1), { ...blankExam(i + 2), result: balanceResult(prev) }, ...prev.slice(i + 1)]
      .map((e, n) => ({ ...e, sort: n + 1 }));
    setExamIndex(i + 1);
    return next;
  }, setExams);
  const moveExam = (i: number, dir: -1 | 1) => edit<PlmExamInput[]>(prev => {
    const j = i + dir;
    const a = prev[i], b = prev[j];
    if (!a || !b) return prev;
    const next = [...prev];
    next[i] = b; next[j] = a;
    setExamIndex(j);
    return next.map((e, n) => ({ ...e, sort: n + 1 }));
  }, setExams);
  const removeExamAt = async (i: number) => {
    if (!exams[i]) return;
    if (!(await confirm({ title: 'Remove', message: 'Remove this component row?', tone: 'danger' }))) return;
    edit<PlmExamInput[]>(prev => prev.filter((_, n) => n !== i).map((e, n) => ({ ...e, sort: n + 1 })), setExams);
    setExamIndex(k => Math.max(0, Math.min(k, exams.length - 2)));
  };

  // ── keyboard (standing rule 14) ────────────────────────────────────────────
  const hotkeys: HotkeyDef[] = [
    { combo: 'mod+s', label: 'Save', group: 'Daily QC', order: 0,
      disabled: !editable || !dirty || busy, run: () => { void doSave(); } },
    { combo: 'alt+u', label: 'Submit', group: 'Daily QC', order: 1,
      disabled: !editable || busy, run: () => { void doSubmit(); } },
    { combo: 'alt+b', label: 'Rollback', group: 'Daily QC', order: 2,
      disabled: !rec?.submittedAt || busy, run: () => { void doRollback(); } },
    { combo: 'esc', label: 'Back', group: 'Daily QC', order: 3, allowInInput: false, run: onBack },
    // The rest of the bar (owner 2026-10-19): the same keys PLM Result Entry uses for the same things.
    { combo: 'alt+n', label: 'New', group: 'Daily QC', order: 4,
      disabled: busy || picking || !!pendingId || chartsOpen, run: () => { void doNew(); } },
    { combo: 'alt+left', label: 'Previous Daily QC', group: 'Daily QC', order: 5,
      run: () => { if ((!picking && !!rec) && index > 0 && !busy && !chartsOpen) step(-1); } },
    { combo: 'alt+right', label: 'Next Daily QC', group: 'Daily QC', order: 6,
      run: () => { if ((!picking && !!rec) && index >= 0 && index < order.length - 1 && !busy && !chartsOpen) step(1); } },
    { combo: 'alt+c', label: 'QC Charts', group: 'Daily QC', order: 7, disabled: chartsOpen, run: () => setChartsOpen(true) },
    { combo: 'alt+m', label: 'Comment', group: 'Daily QC', order: 8,
      disabled: !(!picking && !!rec) || busy || chartsOpen, run: () => { void doComment(); } },
    { combo: 'alt+z', label: 'Reset', group: 'Daily QC', order: 9,
      disabled: !(!picking && !!rec) || !editable || busy || chartsOpen, run: () => { void doReset(); } },
    { combo: 'alt+1', label: 'Stereoscopic Observation tab', group: 'Daily QC', order: 10, run: () => setTab('stereo') },
    { combo: 'alt+2', label: 'PLM Exam tab', group: 'Daily QC', order: 11, run: () => setTab('exam') },
    { combo: 'alt+3', label: 'QC Result View tab', group: 'Daily QC', order: 12, run: () => setTab('result') },
  ];
  useHotkeys(hotkeys);

  // ── render ─────────────────────────────────────────────────────────────────

  if (chartsOpen) return <PlmQcChartsPage initialType="Std" onBack={() => setChartsOpen(false)} />;

  const title = picking ? 'PLM – Daily QC · pick a reference slide' : `PLM – Daily QC ${rec?.dailyQcId ?? ''}`;
  const onRecord = !picking && !!rec;

  // The read's notes (view mode · unsaved · comment) ride the action bar as compact chips instead of a
  // full-width strip each above it (owner 2026-10-19, "This occupied more space"); the full text is the tooltip.
  const noteChips = (
    <>
      {!picking && viewReason && (
        <span className="plm-dq-chip" role="status" title={viewReason}>{viewReason}</span>
      )}
      {onRecord && dirty && editable && (
        <span className="plm-dq-chip is-unsaved" role="status" title="Unsaved changes.">
          ● Unsaved changes
        </span>
      )}
      {onRecord && detail?.comment?.trim() && (
        <span className="plm-dq-chip is-comment" role="note" title={detail.comment.trim()}>💬 {detail.comment.trim()}</span>
      )}
    </>
  );

  const actionBar = (withNotes: boolean) => (
    <AppToolbar
      left={
        <>
          <button className="ca-btn" onClick={onBack} title={`Back (${formatCombo(effectiveCombo('esc'))})`}>
            <span className="tb-i">←</span> Back
          </button>
          <button className="ca-btn" onClick={() => { void doNew(); }} disabled={busy || picking || !!pendingId}
            title={pendingId ? dailyNewBlockedTip(pendingId) : `${DAILY_NEW_TIP} (${formatCombo(effectiveCombo('alt+n'))})`}><span className="tb-i tb-i-green">＋</span> New</button>
          <button className="ca-btn plm-tb-keep-icon" onClick={() => { void doRollback(); }}
            disabled={!rec?.submittedAt || busy}
            title={`Re-open a submitted Daily QC (reason required, audited) (${formatCombo(effectiveCombo('alt+b'))})`}>
            <i className="ti ti-arrow-back-up" aria-hidden="true" /> Rollback
          </button>
        </>
      }
      onDelete={() => { void doDelete(); }}
      deleteDisabled={!onRecord || !canDeleteThis || busy}
      deleteDisabledTitle={DAILY_DELETE_LOCKED_TIP}
      perm={perm}
      beforeRefresh={
        <button className="ca-btn" onClick={() => setChartsOpen(true)} title={`Standard-read control chart (${formatCombo(effectiveCombo('alt+c'))})`}>
          <i className="ti ti-chart-dots" aria-hidden="true" /> QC Charts
        </button>
      }
      onRefresh={() => { void loadLists(); if (oid) void loadRecord(oid); }}
      refreshing={loading}
      right={
        <span className="plm-toolbar-right">
          {withNotes && noteChips}
          <AuditTrailButton formName={AUDIT_FORM} sourceId={rec?.oid} />
          <button className="ca-btn" onClick={() => { void doComment(); }} disabled={!onRecord || busy}
            title={`A comment kept with this Daily QC (${formatCombo(effectiveCombo('alt+m'))})`}>
            <i className="ti ti-message" aria-hidden="true" /> Comment
          </button>
          <button className="ca-btn" onClick={() => { void doReset(); }} disabled={!onRecord || !editable || busy}
            title={`Discard all unsaved changes on this Daily QC (${formatCombo(effectiveCombo('alt+z'))})`}>
            <span className="tb-i">↺</span> Reset
          </button>
          <button className="ca-btn" onClick={() => { void doSave(); }} disabled={!onRecord || !editable || busy}
            title={`Save your read — the result is shown after Submit (${formatCombo(effectiveCombo('mod+s'))})`}>
            <span className="tb-i">💾</span> Save
          </button>
          <button className="ca-btn ca-btn-solid" onClick={() => { void doSubmit(); }} disabled={!onRecord || !editable || busy}
            title={`Submit — the read is locked; a pass unlocks your sample layers for the day (${formatCombo(effectiveCombo('alt+u'))})`}>
            <span className="tb-i">⬆</span> Submit
          </button>
        </span>
      }
    />
  );

  return (
    <div className="plm-root plm-dq-page">
      <div className="dp-breadcrumb">
        <span>Home</span><i className="ti ti-chevron-right" />
        <span>Analysis</span><i className="ti ti-chevron-right" />
        <span>PLM</span><i className="ti ti-chevron-right" />
        <span className="dp-bc-active">Daily QC</span>
      </div>
      <FormTitle pageId="plm" title={title} loadMs={loadMs} loading={loading} />
      {/* Previous / Next as the ‹ › pair in the top bar, as on PLM Result Entry and the prototype
          (owner 2026-10-19); Alt+← / Alt+→ step as well. */}
      <PlmTopbarMeta loadMs={null} dirty={false} arrowsOnly alwaysArrows={onRecord} itemLabel="Daily QC"
        onPrev={onRecord && index > 0 && !busy ? () => step(-1) : undefined}
        onNext={onRecord && index >= 0 && index < order.length - 1 && !busy ? () => step(1) : undefined} />
      {actionBar(true)}

      {/* G5 parity (owner, 2026-10-17): the read opens on its own, full width — no
          list of Daily QC IDs beside it. The list page is the history; Previous /
          Next on the bar step through the reads. */}
      <div className="plm-body plm-body--full">
        {/* G5 New: the "Reference Slide" window, a popup over the form. */}
        <PlmDailySlidePickerDialog
          open={picking}
          onClose={() => { setPicking(false); if (!oid) onBack(); }}
          onCreated={created => { setPicking(false); setEntering(true); setOid(created.oid); void loadLists(); }}
        />

        <main className="plm-main">
          {picking || !rec || !draft ? (
            <div className="plm-exam__empty">
              {picking ? 'Pick the reference slide you will read today.' : 'Select a Daily QC record.'}
            </div>
          ) : (
            <>
              {/* G5 PLMV2_DailyEntry header (owner, 2026-10-17): three columns —
                  the read and its slide · who analysed / entered it and when · the
                  four devices of PLM Result Entry and Scope Alignment. Dispersion
                  Oil / Temperature are no longer shown here (a stored value is kept). */}
              <div className="plm-context plm-dq-ctx plm-hdr-std">
                <div className="plm-context__col">
                  <div className="plm-context__item"><label>Daily QC ID</label><span>{rec.dailyQcId}</span></div>
                  <div className="plm-context__item">
                    <label htmlFor="plm-dq-slide">Reference Slide ID</label>
                    {/* A pick list on the analyst's own pending read being entered; text otherwise (owner 2026-10-19, G5 parity). */}
                    {editable ? (
                      <LookupCombo
                        id="plm-dq-slide"
                        lookup={slideLookup}
                        value={pickedSlide ? slideLabel(pickedSlide.referenceId, pickedSlide.originalSourceId)
                          : slideLabel(rec.referenceId, rec.originalSourceId)}
                        rowKey={row => slideLabel(row[0], row[1])}
                        popupWidth={640}
                        placeholder=""
                        onPick={row => {
                          const label = slideLabel(row[0], row[1]);
                          const slide = slides.find(x => slideLabel(x.referenceId, x.originalSourceId) === label) ?? null;
                          const own = label === slideLabel(rec.referenceId, rec.originalSourceId);
                          setPickedSlide(own ? null : slide);
                          setDirty(true);
                        }}
                      />
                    ) : (
                      <span>{slideLabel(rec.referenceId, rec.originalSourceId)}</span>
                    )}
                  </div>
                  <div className="plm-context__item"><label>Matrix</label><span>{detail?.slideMatrix ?? ''}</span></div>
                  <div className="plm-context__item"><label>Source</label><span>{detail?.slideSource ?? ''}</span></div>
                </div>
                <div className="plm-context__col">
                  <div className="plm-context__item">
                    <label htmlFor="plm-dq-analyzed-date">Analyzed Date</label>
                    <input
                      id="plm-dq-analyzed-date"
                      type="datetime-local"
                      className="plm-input"
                      /* ISO yyyy-MM-ddTHH:mm, sliced (never parsed) so the stored
                         wall-clock is not shifted (§14b.6 kill-switch). */
                      value={(draft.analyzedDate ?? '').slice(0, 16)}
                      disabled={!editable}
                      onChange={e => {
                        setDraft({ ...draft, analyzedDate: e.target.value ? `${e.target.value}:00` : null });
                        setDirty(true);
                      }}
                    />
                  </div>
                  <div className="plm-context__item">
                    <label>Analyzed By</label>
                    {/* G5 offers only the read's own analyst (AnalystSource). */}
                    <ComboSelect
                      value={draft.analyzedByOid ?? ''}
                      options={[{ value: rec.analystOid, label: rec.analystName ?? '' }]}
                      onChange={v => { setDraft({ ...draft, analyzedByOid: v || null }); setDirty(true); }}
                      placeholder=""
                      disabled={!editable}
                    />
                  </div>
                  <div className="plm-context__item">
                    <label>Entered Date</label>
                    <span title="When the read was started">{fmtLocalDateTime(rec.createdAt)}</span>
                  </div>
                  <div className="plm-context__item">
                    <label>Entered By</label>
                    <span>{rec.analystName ?? ''}</span>
                  </div>
                </div>
                <div className="plm-context__col">
                  {DEVICES.map(([field, label, req]) => (
                    <div className="plm-context__item" key={field}>
                      <label htmlFor={`plm-dev-${field}`}>
                        {label}{req && <span className="plm-req" aria-hidden="true">*</span>}
                      </label>
                      {stationRows.length > 0 ? (
                        <PlmDeviceCombo
                          field={field}
                          draft={draft}
                          rows={stationRows}
                          disabled={!editable}
                          required={req}
                          onChange={next => { setDraft(next); setDirty(true); }}
                        />
                      ) : (
                        // No setup rows: the remembered names, pick-only as on PLM Result Entry (owner 2026-10-19, G5 parity).
                        <PlmDevicePick
                          id={`plm-dev-${field}`}
                          className={req && !draft[field]?.trim() ? 'is-reqempty' : undefined}
                          ariaLabel={label}
                          names={deviceNames(field, utilities)}
                          value={draft[field]}
                          disabled={!editable}
                          onChange={v => { setDraft({ ...draft, [field]: v }); setDirty(true); }} />
                      )}
                    </div>
                  ))}
                  {/* A header row like the others (owner 2026-10-19, "Header section need correction"):
                      the label in the label column, the tick and its note in the field column. */}
                  <div className="plm-context__item">
                    <label htmlFor="plm-dq-align">
                      Scope Alignment<span className="plm-req" aria-hidden="true">*</span>
                    </label>
                    <label className="plm-dq-align">
                      <input id="plm-dq-align" type="checkbox" checked={scopeAligned} disabled={!editable}
                        onChange={e => { setScopeAligned(e.target.checked); setDirty(true); }} />
                      <span className="plm-muted">Aligned and checked today</span>
                    </label>
                  </div>
                </div>
              </div>
              <div className="plm-tabs" role="tablist">
                <button role="tab" aria-selected={tab === 'stereo'} onClick={() => setTab('stereo')}
                  className={`plm-tab${tab === 'stereo' ? ' is-active' : ''}`}>Stereoscopic Observation</button>
                <button role="tab" aria-selected={tab === 'exam'} onClick={() => setTab('exam')}
                  className={`plm-tab${tab === 'exam' ? ' is-active' : ''}`}>PLM Exam</button>
                <button role="tab" aria-selected={tab === 'result'} onClick={() => setTab('result')}
                  className={`plm-tab${tab === 'result' ? ' is-active' : ''}`}>QC Result View</button>
              </div>
              <div className="plm-tabpanel">
                {tab === 'stereo' ? (
                  <PlmStereoTab
                    value={draft}
                    onChange={next => { setDraft(next); setDirty(true); }}
                    materials={lookups.materials}
                    readOnly={!editable}
                  />
                ) : tab === 'exam' ? (
                  <PlmExamTab
                    exams={exams}
                    onChange={next => { setExams(next); setDirty(true); }}
                    activeIndex={examIndex}
                    onActiveIndexChange={setExamIndex}
                    lookups={lookups}
                    fiberTypeValues={fiberTypeValues}
                    readOnly={!editable}
                    pointCountType={null}
                    onPointCountTypeChange={() => {}}
                    onOpenPointCount={() => {}}
                    onAddExam={addExam}
                    onRemoveExam={() => { void removeExamAt(examIndex); }}
                    onAddExamAt={addExamAt}
                    onRemoveExamAt={i => { void removeExamAt(i); }}
                    onMoveExam={moveExam}
                    pointCountDisabled
                  />
                ) : (
                  <DailyQcResult detail={detail!}
                    canOverride={canOverrideThis} onOverride={() => { void doOverride(); }}
                    busy={busy} />
                )}
              </div>
            </>
          )}
        </main>
      </div>
      <div className="plm-actbar-bottom">{actionBar(false)}</div>
    </div>
  );
}

/** The QC Result View of a Daily QC (G5 PlmV2Rules.DailyPerformanceHtml): the
 *  verdict, the checks and one row per reference asbestos type — shown only after
 *  Submit, never after a Save (owner 2026-10-20; the API sends none before). */
function DailyQcResult({ detail, canOverride, onOverride, busy }: {
  detail: PlmDailyQcDetail; canOverride: boolean;
  onOverride: () => void; busy: boolean;
}) {
  const r = detail.record;
  const p = detail.saved && r.submittedAt ? detail.performance : null;
  if (!p) {
    return (
      <div className="plm-exam__empty">
        The result against reference slide <b>{r.referenceId}</b> is shown after you <b>Submit</b> the read.
      </div>
    );
  }
  const pass = p.result === 'Pass';
  const submitted = !!r.submittedAt;
  const analyzed = detail.observation?.analyzedDate ? fmtLocalDate(detail.observation.analyzedDate) : null;
  const allNd = p.rows.every(row => /^(nd|none( detected)?)$/i.test(row.type.trim()));
  return (
    <div className="plm-dq-result">
      <div className={`plm-dqres ${pass ? 'is-pass' : 'is-fail'}`}>
        <div className="plm-dqres__v">{pass ? 'PASS' : 'FAIL'}</div>
        <div className="plm-dqres__d">
          {r.dailyQcId} · {r.referenceId}{r.originalSourceId ? ` (${r.originalSourceId})` : ''} · {r.analystName}
          {analyzed && ` · ${analyzed}`}
          {submitted ? <> · <b>Submitted</b></> : ' · not submitted yet'}
          {r.reopened && ' · re-opened once'}
        </div>
        {submitted && !pass && canOverride && (
          <button type="button" className="ca-btn" onClick={onOverride} disabled={busy}>Supervisor override</button>
        )}
      </div>
      <div className="plm-qcchecks">
        {p.checks.map(c => (
          <span key={c.label} className={`plm-qcchk ${c.ok ? 'is-ok' : 'is-bad'}`}>{c.ok ? '✓' : '✗'} {c.label}</span>
        ))}
      </div>
      <table className="plm-dq-table">
        <thead>
          <tr>
            <th>Asbestos Type</th><th className="num">Reference %</th><th className="num">Acceptable Range</th>
            <th className="num">Read %</th><th>RI α (limits)</th><th>RI γ (limits)</th><th>Result</th>
          </tr>
        </thead>
        <tbody>
          {p.rows.map(row => (
            <tr key={row.type}>
              <td><b>{row.type}</b></td>
              <td className="num">{num3(row.value)}</td>
              <td className="num">{num3(row.low)}–{num3(row.high)}%</td>
              <td className={`num ${row.idOk && row.pctOk ? 'is-ok' : 'is-bad'}`}>{row.read}</td>
              <td className={row.riOk ? '' : 'is-bad'}>
                {row.alpha}{row.hasRi && <span className="plm-dq-lim"> ({num3(row.riALow)}–{num3(row.riAHigh)})</span>}
              </td>
              <td className={row.riOk ? '' : 'is-bad'}>
                {row.gamma}{row.hasRi && <span className="plm-dq-lim"> ({num3(row.riGLow)}–{num3(row.riGHigh)})</span>}
              </td>
              <td className={row.ok ? 'is-ok' : 'is-bad'}>{row.ok ? 'Pass' : 'Fail'}</td>
            </tr>
          ))}
          {!allNd && p.extra.map((x, i) => (
            <tr key={`extra-${i}`}>
              <td><b>{x.name?.trim() || '?'}</b></td><td className="num">—</td><td className="num">—</td>
              <td className="num is-bad">{x.result}%</td><td>—</td><td>—</td><td className="is-bad">Not in reference</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="plm-dq-foot">
        Range rule: below 15% → ± 5, 15–100% → ± 20 (percentage points, clamped 0–100).
        {p.rows.length > 1 && ' Multi-type slide: every type must pass.'}
      </div>
      {/* No "Recent Daily QC" table under the result (owner 2026-10-19, as the G5 screen shows it):
          the PLM Daily QC list is the history. */}
    </div>
  );
}

